// @flow
import moment from 'moment';
import { parseCurrency, round } from './helpers';
import {
  defaultPersonalInterestRate,
  defaultAdminFeeRate,
  defaultInsuranceFeeRate
} from './constants';
import type { Simulation, Rows, Stats } from './types';

/**
 * Simulador de Préstamos Personales.
 * Usamos el sistema de interés Directo y pagos mensuales.
 * Detalles del cálculo en la Wiki.
 *
 * @param {number} amount - capital, el monto del préstamo solicitado por el cliente
 * @param {number} duration - cantidad de cuotas o plazo del préstamo, en meses
 * @param {number} interestRate - tasa de interés mensual para préstamos personales
 * @param {number} adminFeeRate - porcentaje del capital que se cobra como gastos administrativos
 * @param {number} insuranceFeeRate - porcentaje del capital que se cobra en concepto de seguro
 */

export default function personal(
  amount: number,
  duration: number,
  interestRate: number = defaultPersonalInterestRate,
  adminFeeRate: number = defaultAdminFeeRate,
  insuranceFeeRate: number = defaultInsuranceFeeRate
): Simulation {
  // Estos valores son independientes del período,
  // i.e.: constantes para todas las filas de la tabla

  // Amortización
  const principal: number = amount / duration;

  // Interés
  // TNA = 2,60 mensual / 30 días = 0,8666 diario * 365 dias al año = 31,63333
  // Nosotros consideramos como interés a la TNA dividido 12 meses al año.
  const nominalInterestRate = (interestRate * 365) / 30; // TNA
  const interest: number = (amount * nominalInterestRate) / 12;

  // Gastos administrativos, en total corresponden al 1% del capital.
  // Lo que hacemos es dividir ese total por la cantidad de períodos.
  // Esto es diferente del seguro:
  // - adminFeeRate es totalizado, y tiene que dividirse por la cantidad de períodos.
  // - insuranceFeeRate es mensual, no hay que dividirlo,
  // es lo que se paga en cada cuota en concepto de seguro.
  const adminFee: number = (amount * adminFeeRate) / duration;

  // Seguro, 0.1% MENSUAL.
  // Por cada cuota, se paga 0.1% del capital en concepto de Seguro.
  const insuranceFee: number = amount * insuranceFeeRate;

  // Valor de la cuota real.
  // Para la cuota pura solo considerar principal + interest.
  const payment: number = principal + interest + adminFee + insuranceFee;

  // Total a pagar por el préstamo
  const total: number = payment * duration;

  // Detalle de cuotas
  const rows: Rows = Array(duration)
    .fill(0)
    .map((row: number, index: number) => {
      // Número de cuota
      const term: number = index + 1;

      // Vencimiento
      const dueDate: string = moment()
        .add(index + 1, 'months')
        .format('DD/MM/YYYY');

      // Total amortizado
      const accumulatedPrincipal: number = (index + 1) * principal;

      // Deuda a la fecha
      const balance: number = total - index * payment;

      return [
        term,
        dueDate,
        parseCurrency(balance),
        parseCurrency(principal),
        parseCurrency(interest),
        parseCurrency(adminFee),
        parseCurrency(insuranceFee),
        parseCurrency(payment),
        parseCurrency(accumulatedPrincipal)
      ];
    });

  const stats: Stats = {
    type: 'Directo',
    amount: parseCurrency(amount),
    accrued: parseCurrency((principal + interest) * duration - amount),
    duration: `${duration} meses`,
    nominalInterestRate: `${round(nominalInterestRate * 100)}%`,
    monthlyInterestRate: `${round(interestRate * 100)}%`,
    stateTax: parseCurrency(amount * 0.012),
    actual: parseCurrency(amount - amount * 0.012),
    admin: parseCurrency(adminFee * duration),
    insurance: parseCurrency(insuranceFee * duration),
    averagePayment: parseCurrency(payment),
    total: parseCurrency(total)
  };

  return {
    stats,
    rows
  };
}
